import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, ArrowRight, BookOpen, MessageCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { blogPosts } from '../blogData';

const WA = 'https://wa.me/5516991378312?text=' + encodeURIComponent('Olá! Li um artigo no blog da Fernando Tech e gostaria de mais informações.');

const fmtData = (iso) => new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });

const ArticleJsonLd = ({ post }) => {
  const json = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.titulo,
    description: post.descricao,
    datePublished: post.data,
    inLanguage: 'pt-BR',
    author: { '@type': 'Organization', name: 'Fernando Tech', url: 'https://fernandotech.com.br' },
    publisher: { '@type': 'Organization', name: 'Fernando Tech' },
    mainEntityOfPage: `https://fernandotech.com.br/blog/${post.slug}`,
  };
  return <script type="application/ld+json">{JSON.stringify(json)}</script>;
};

export default function BlogPage() {
  const { slug } = useParams();
  const post = slug ? blogPosts.find(p => p.slug === slug) : null;

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = post
      ? `${post.titulo} | Blog Fernando Tech`
      : 'Blog de Dicas de TI e Manutenção | Fernando Tech';
  }, [post]);

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <main className="pt-40 pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          {!post ? (
            <>
              <header className="mb-10 text-center">
                <span className="inline-flex items-center gap-1.5 bg-cyan-500/15 text-cyan-400 text-xs font-semibold px-3 py-1 rounded-full mb-4">
                  <BookOpen className="w-3.5 h-3.5" /> Blog Fernando Tech
                </span>
                <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3">Dicas de TI e Manutenção</h1>
                <p className="text-slate-400 text-base max-w-2xl mx-auto">Artigos práticos sobre computadores, segurança eletrônica e gestão de oficinas — direto de quem atende empresas todos os dias.</p>
              </header>
              <div className="space-y-5" data-testid="blog-list">
                {blogPosts.map(p => (
                  <Link key={p.slug} to={`/blog/${p.slug}`}
                    className="block bg-slate-900 border border-slate-800 hover:border-cyan-500/40 rounded-2xl p-6 transition-all hover:-translate-y-0.5"
                    data-testid={`blog-card-${p.slug}`}>
                    <span className="text-cyan-400 text-xs font-semibold uppercase tracking-wide">{p.categoria}</span>
                    <h2 className="text-lg md:text-lg font-bold text-white mt-2 mb-2">{p.titulo}</h2>
                    <p className="text-slate-400 text-sm mb-3">{p.descricao}</p>
                    <div className="flex items-center gap-4 text-slate-500 text-xs">
                      <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{fmtData(p.data)}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{p.tempoLeitura} de leitura</span>
                      <span className="ml-auto text-cyan-400 flex items-center gap-1">Ler artigo <ArrowRight className="w-3.5 h-3.5" /></span>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <article data-testid="blog-article">
              <ArticleJsonLd post={post} />
              <Link to="/blog" className="inline-flex items-center gap-1.5 text-cyan-400 text-sm mb-6 hover:text-cyan-300" data-testid="blog-back-btn">
                <ArrowLeft className="w-4 h-4" /> Voltar ao Blog
              </Link>
              <span className="block text-cyan-400 text-xs font-semibold uppercase tracking-wide mb-3">{post.categoria}</span>
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">{post.titulo}</h1>
              <div className="flex items-center gap-4 text-slate-500 text-xs mb-10">
                <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{fmtData(post.data)}</span>
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{post.tempoLeitura} de leitura</span>
              </div>
              {post.secoes.map((s, i) => (
                <section key={i} className="mb-8">
                  <h2 className="text-lg font-bold text-white mb-3">{s.h2}</h2>
                  {s.paragrafos.map((p, j) => (
                    <p key={j} className="text-slate-300 text-sm leading-relaxed mb-3">{p}</p>
                  ))}
                </section>
              ))}
              <div className="mt-12 bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border border-cyan-500/20 rounded-2xl p-8 text-center">
                <h2 className="text-lg font-bold text-white mb-2">Precisa de ajuda com isso?</h2>
                <p className="text-slate-400 text-sm mb-5">Fale com a Fernando Tech — diagnóstico gratuito em Ribeirão Preto e região, suporte remoto para todo o Brasil.</p>
                <a href={WA} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white font-bold px-6 py-3 rounded-xl text-sm transition-colors"
                  data-testid="blog-cta-whatsapp">
                  <MessageCircle className="w-4 h-4" /> Chamar no WhatsApp
                </a>
              </div>
            </article>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
